/**
 * Nota comercial creada desde una línea de gastos del embarque.
 *
 * El rol del contacto sale del tipo de la línea: la factura (o la proforma) del proveedor
 * es «Supplier», y el resto de los gastos llevan su propio tipo como rol — Freight para el
 * flete, Courrier para el courier, y así. Para los asientos y los saldos, todo rol que no
 * sea Client cuenta del lado del proveedor (ver notaComercialRoleSide en el backend).
 */

/** Rol canónico del cliente y del proveedor (mismos valores que guarda el backend). */
export const NOTA_COM_CLIENT_ROLE = "Client";
export const NOTA_COM_SUPPLIER_ROLE = "Supplier";
/** La nota del proveedor la emite él y nosotros la registramos. */
export const NOTA_COM_RECEIVING = "Receiving";

/**
 * Tipos de gasto que son el pago al proveedor de la mercadería: ahí el rol es «Supplier»
 * y no el nombre del tipo. Los ids están sembrados iguales en todos los tenants
 * (ver invoiceSupplierExpenseHelpers en el backend).
 */
export const SUPPLIER_EXPENSE_TYPE_IDS = [
  "683b31db6094fed22e74537e", // Invoice Supplier
  "683b31db6094fed22e7453f0", // Proform Supplier
];

function refIdString(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") return String(value._id || "");
  return String(value);
}

/**
 * Etiqueta del tipo de la línea, ya venga poblado o como id a resolver en las listas.
 * @param {object} row línea de shipping_expenses
 * @param {Array} lists listas del tenant (cada una con type y values)
 * @returns {string}
 */
export function expenseTypeLabel(row, lists = []) {
  const type = row && row.type;
  if (type && typeof type === "object" && (type.label || type.name)) {
    return String(type.label || type.name);
  }
  const id = refIdString(type);
  if (!id) return "";
  const list = (Array.isArray(lists) ? lists : []).find(
    (l) => l && String(l.type || "") === "typeofpayment"
  );
  const value = ((list && list.values) || []).find((v) => v && String(v._id) === id);
  return value && value.label ? String(value.label) : "";
}

/**
 * Rol del contacto para una línea de gasto.
 * @param {object} row
 * @param {Array} lists
 * @returns {string} '' si no se puede deducir
 */
export function contactRoleForExpenseLine(row, lists = []) {
  const typeId = refIdString(row && row.type);
  if (typeId && SUPPLIER_EXPENSE_TYPE_IDS.includes(typeId)) return NOTA_COM_SUPPLIER_ROLE;
  const label = expenseTypeLabel(row, lists);
  // El separador del textlist es "|": una etiqueta que lo lleve no puede ser una opción.
  if (label && !label.includes("|")) return label;
  return typeId ? NOTA_COM_SUPPLIER_ROLE : "";
}

/**
 * Lo que falta pagar de una línea de gasto, o null si no se puede saber.
 *
 * El backend deja `remaining` calculado; si no está, se cae al importe menos lo ya
 * aplicado o pagado. Es el tope de lo que una nota puede descontarle: de ahí para
 * arriba el saldo del gasto quedaría en negativo.
 *
 * @param {object} row línea de shipping_expenses
 * @returns {number|null}
 */
export function expenseLinePendingAmount(row) {
  if (!row) return null;
  const remaining = Number(row.remaining);
  if (Number.isFinite(remaining)) return Math.max(0, remaining);
  const amount = Number(row.amount);
  if (!Number.isFinite(amount)) return null;
  const paid = Number(row.paid);
  return Math.max(0, amount - (Number.isFinite(paid) ? paid : 0));
}

/** Una línea de gastos del embarque, no cualquier fila de grupo. */
export function isShippingExpenseRow(row) {
  return !!(row && typeof row === "object" && row._id && (row.shipping || row.parent) && "type" in row);
}

/**
 * Contexto para abrir el popup de nota comercial desde una línea de gastos.
 *
 * Nace siempre como nota de crédito que nos emite el proveedor (Credit note + Receiving),
 * con el contacto y el rol de la línea, su moneda, y aplicada al embarque de esa línea.
 *
 * @param {object} row línea de shipping_expenses
 * @param {Array} lists listas del tenant
 * @param {{ shippingName?: string }} [options]
 * @returns {object} contexto para NotaComercialNoteModal.open()
 */
export function notaComercialModalContextFromExpenseLine(row, lists = [], options = {}) {
  const shippingId = refIdString(row && (row.shipping || row.parent));
  const context = {
    noteKind: "supplier_credit_to_us",
    lockContact: false,
    namePrefix: "NC",
  };
  const contactId = refIdString(row && row.contact);
  if (contactId) {
    context.contactId = contactId;
    context.supplierId = contactId;
  }
  const role = contactRoleForExpenseLine(row, lists);
  if (role && role !== NOTA_COM_CLIENT_ROLE) context.contactRoleOverride = role;
  const currencyId = refIdString(row && (row.currencyex || row.currency));
  if (currencyId) context.currencyId = currencyId;
  // Solo se aplica lo que falta pagar de esta línea: más la dejaría en negativo.
  const pending = expenseLinePendingAmount(row);
  if (pending !== null) context.maxApplicable = pending;
  if (shippingId) {
    context.appliedTo = {
      type: "shipping",
      id: shippingId,
      name: options.shippingName || "",
    };
  }
  return context;
}
